import React, { useState, useRef } from "react";
import { Add } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Table from "../../components/Table/Table";
import {
  useUserTrainingRecords,
  createTrainingRecord,
  updateTrainingRecord,
  useTrainingRecordProgress
} from "../../hooks/employees";
import { visionURL } from "../../config/environment";
import { Box, Menu, MenuItem, IconButton } from "@mui/material";
import { getSortingParam } from "../../utils/tableUtils";
import CourseTypeIcon from "../../components/Icons/CourseTypeIcon";
import AdminScoreCell from "../../components/ScoreCell/AdminScoreCell";
import VistoLogoIcon from "../../components/Icons/VistoLogoIcon";
import OtherTrainingRecordModal from "./OtherTrainingRecordModal";
import { formatDate } from "../../utils/datetime";
import { cloneDeep } from "lodash";
import { uploadFile } from "../../services/fileUploadService";
import {
  enqueueSuccessSnackbar,
  enqueueErrorSnackbar,
  enqueueWarningSnackbar
} from "../../redux/actions/snackbarActions";
import AttachmentIcon from "@mui/icons-material/Attachment";
import MyAchievementsButtons from "../Performance/MyAchievementsButtons";
import { iltClassTypes, assignmentStatuses } from "../../constants";
import HomeworkIcon from "../../components/Icons/HomeworkIcon";
import {
  buildHomeworkSubmissionRoute,
  buildLegacySubmissionRoute
} from "../../routes/AdminRoutes";
import CourseProgressModal from "./CourseProgressModal";
import { Tooltip } from "@mui/material";
import TooltipButton from "../../components/Buttons/TooltipButton";
import usePermissions from "../../redux/selectors/usePermissions";

const openHomeworkView = submission => {
  const routeBuilder = {
    homeworkSubmission: buildHomeworkSubmissionRoute,
    brspSubmission: buildLegacySubmissionRoute
  }[submission.type];
  window.open(routeBuilder(submission.id), "_blank");
};

const ActionsCell = ({ trainingRecord, openCourseProgressModal }) => {
  const { t } = useTranslation();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const homeworkSubmissions = [
    ...new Set([
      ...(trainingRecord.homeworkSubmissions || []),
      ...(trainingRecord.brspSubmissions || [])
    ])
  ];

  const showHomeworkButton = homeworkSubmissions.length > 0;
  const hasMultipleHomeworkAssignments = homeworkSubmissions.length > 1;
  const showCompletionDetailsButton = trainingRecord.displayCompletionDetails;

  const handleViewSubmissionBtnClick = event => {
    if (!hasMultipleHomeworkAssignments) {
      openHomeworkView(homeworkSubmissions[0]);
    } else {
      // Show dropdown menu to display multiple homework assignments for the admin to choose from
      setMenuAnchorEl(event.currentTarget);
    }
  };

  const handleSelectSubmission = submission => {
    setMenuAnchorEl(null);
    openHomeworkView(submission);
  };

  return (
    <Box
      display="flex"
      height="0px"
      alignItems="center"
      data-qa="employee-transcript-actions-cell"
      flex={1}
      justifyContent="space-between"
    >
      <Box ml={-2} mr={2}>
        {showCompletionDetailsButton && (
          <TooltipButton
            variant="text"
            disabled={trainingRecord.manualCompletion}
            onClick={e => openCourseProgressModal(e, trainingRecord)}
            tooltipProps={{
              title:
                trainingRecord.manualCompletion &&
                t("administration.manualCompletionMessage")
            }}
            data-qa="completion-details-btn"
          >
            {t("administration.completionDetails")}
          </TooltipButton>
        )}
      </Box>
      <Box display="flex" justifyContent="space-between" flex={1}>
        <Box>
          {showHomeworkButton && (
            <>
              <Tooltip title={t("administration.homeworkButtonTooltip")}>
                <div>
                  <IconButton
                    color="primary"
                    onClick={handleViewSubmissionBtnClick}
                    data-qa="employee-transcript-homework-btn"
                    size="large"
                  >
                    <HomeworkIcon />
                  </IconButton>
                </div>
              </Tooltip>
              {hasMultipleHomeworkAssignments && (
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={() => setMenuAnchorEl(null)}
                  keepMounted
                  data-qa="employee-transcript-homework-btn-menu"
                >
                  {homeworkSubmissions.map(submission => (
                    <MenuItem
                      key={submission.id}
                      onClick={() => handleSelectSubmission(submission)}
                    >
                      {submission.type === "brspSubmission"
                        ? submission.filename
                        : submission.homework.title}
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </>
          )}
        </Box>
        {!!trainingRecord.hasCertificate && trainingRecord.isPassed && (
          <MyAchievementsButtons
            isIlt={iltClassTypes.includes(
              trainingRecord.trainingObject.courseType
            )}
            isActive={!trainingRecord.trainingObject.isClosed}
            isAttended={trainingRecord.isAttended}
            isPassed={trainingRecord.isPassed}
            printUrl={`${visionURL}/certificates/print/${trainingRecord.id}`}
            emailModalType="certificate"
            dlgKey={trainingRecord.id}
            trainingRecordId={trainingRecord.id}
          />
        )}
      </Box>
    </Box>
  );
};

const columns = (t, openCourseProgressModal) => [
  {
    title: "Type",
    field: "courseType",
    cellStyle: {
      borderBottom: "none",
      textAlign: "center",
      padding: "6px 8px 0 0"
    },
    headerStyle: {
      paddingLeft: 16
    },
    render: ({ trainingObject }) => (
      <CourseTypeIcon type={trainingObject.courseType} />
    ),
    customSort: () => 0
  },
  {
    title: "Title",
    field: "trainingObject.name",
    options: {
      sorting: true,
      filtering: true
    },
    headerStyle: {
      paddingLeft: 0
    },
    cellStyle: {
      minWidth: 200,
      paddingLeft: 0
    },
    render: ({ title, fromVisto }) => (
      <Box whiteSpace="nowrap">
        {title}
        {fromVisto && (
          <Box component="span" ml={1}>
            <VistoLogoIcon style={{ marginBottom: -1 }} />
          </Box>
        )}
      </Box>
    )
  },
  {
    title: "score",
    field: "score",
    options: {
      sorting: true,
      filtering: true
    },
    cellStyle: {
      minWidth: 120
    },
    render: rowData => (
      <AdminScoreCell
        rowIndex={rowData.tableData.id}
        scoreData={rowData.score}
        postScore={rowData.postScore}
        isEditable={false}
      />
    )
  },
  {
    title: "dateCompleted",
    field: "completedAt",
    options: {
      sorting: true,
      filtering: true,
      filterType: "date"
    },
    cellStyle: {
      width: 190
    },
    render: ({ completedAt }) =>
      completedAt ? formatDate(completedAt) : t("inProgress")
  },
  {
    field: "certificate",
    cellStyle: {
      width: 72
    },
    render: ({ certificate }) => (certificate ? <AttachmentIcon /> : null)
  },
  {
    title: "actions",
    cellStyle: {
      width: 150
    },
    render: trainingRecord => (
      <ActionsCell
        trainingRecord={trainingRecord}
        openCourseProgressModal={openCourseProgressModal}
      />
    )
  }
];

const EmployeeTranscript = ({ employeeId }) => {
  const tableRef = useRef(null);
  const { t } = useTranslation();
  const { getUserTrainingRecords } = useUserTrainingRecords();
  const { isGoldOrPlatinum } = usePermissions();
  const [otherTRModalOpen, setOtherTRModalOpen] = useState(false);
  const [isSubmittingTrainingRecord, setIsSubmittingTrainingRecord] = useState(
    false
  );
  const [currentTrainingRecord, setCurrentTrainingRecord] = useState(null);
  const { createOtherTrainingRecord } = createTrainingRecord();
  const { updateOtherTrainingRecord } = updateTrainingRecord();
  const [currentProgress, setCurrentProgress] = useState(null);
  const [currentRecordName, setCurrentRecordName] = useState(null);
  const [completedActivities, setCompletedActivities] = useState(0);
  const [totalCompletion, setTotalCompletion] = useState(0);
  const [viewDetailsModalOpen, setViewDetailsModalOpen] = useState(false);
  const { getTrainingRecordProgress, loading } = useTrainingRecordProgress();
  const dispatch = useDispatch();

  const handleRowClick = (e, rowData) => {
    if (rowData.trainingObject.courseType === "Other") {
      setCurrentTrainingRecord(rowData);
      setOtherTRModalOpen(true);
    }
  };

  const handleClose = () => {
    setOtherTRModalOpen(false);
    setCurrentTrainingRecord(null);
  };

  const fetchTrainingRecords = ({
    orderBy,
    orderDirection,
    page,
    pageSize,
    filters
  }) => {
    const tableFilterData = cloneDeep(filters);
    if (filters) {
      const dateFilterIndex = tableFilterData.findIndex(
        filter => filter.column.title === "dateCompleted"
      );
      if (dateFilterIndex > -1)
        tableFilterData[dateFilterIndex].value = formatDate(
          tableFilterData[dateFilterIndex].value,
          "DD/MM/YYYY"
        );
    }
    const orderByColumn = orderBy ? orderBy.field : null;
    const records = getUserTrainingRecords({
      employeeId,
      sort: getSortingParam(orderByColumn, orderDirection),
      page: { number: page, size: pageSize },
      filters: tableFilterData.reduce(
        (currentFilters, { column, value }) => ({
          ...currentFilters,
          [column.field]: value
        }),
        {}
      )
    }).then(result => {
      if (result.error) {
        dispatch(
          enqueueErrorSnackbar({
            message: t("employees.transcript.errorMessage")
          })
        );
        return new Promise((resolve, reject) => {
          resolve({ data: [], page: 0, totalCount: 0 });
        });
      }
      const hasErrors = result?.data?.some(
        record => record.brspDetailsUnavailable
      );
      if (hasErrors) {
        dispatch(
          enqueueWarningSnackbar({
            message: t("employees.transcript.noDetailsWarning")
          })
        );
      }
      return new Promise((resolve, reject) => {
        resolve(result);
      });
    });

    return records;
  };

  const openCourseProgressModal = (e, trainingRecord) => {
    setCurrentRecordName(trainingRecord.title);
    getTrainingRecordProgress(employeeId, trainingRecord.id).then(data => {
      const completed = data.filter(
        p => p.status === assignmentStatuses.completed
      ).length;
      setCurrentProgress(data);
      setCompletedActivities(completed);
      setTotalCompletion(
        trainingRecord.isScorm
          ? 100
          : Math.round((completed / data.length) * 100)
      );
    });
    setViewDetailsModalOpen(true);
    e.stopPropagation();
  };

  const handleSubmitTrainingRecordSuccess = successMsg => {
    setIsSubmittingTrainingRecord(false);
    handleClose();
    dispatch(enqueueSuccessSnackbar({ message: successMsg }));
    tableRef.current.onQueryChange();
  };

  const handleAddNewTrainingRecord = requestData => {
    createOtherTrainingRecord(
      {
        name: requestData.name,
        score: requestData.score,
        postScore: requestData.postScore,
        durationHours: requestData.durationHours,
        durationMinutes: requestData.durationMinutes,
        completionDate: requestData.completionDate,
        description: requestData.description,
        certificate: requestData.certificate
      },
      employeeId
    ).then(() => {
      handleSubmitTrainingRecordSuccess(
        t("otherTrainingRecords.createSuccessMsg")
      );
    });
  };

  const handleUpdateTrainingRecord = requestData => {
    updateOtherTrainingRecord(employeeId, currentTrainingRecord.id, {
      name: requestData.name,
      score: requestData.score,
      postScore: requestData.postScore,
      durationHours: requestData.durationHours,
      durationMinutes: requestData.durationMinutes,
      completionDate: requestData.completionDate,
      description: requestData.description,
      certificate: requestData.certificate
    }).then(() => {
      handleSubmitTrainingRecordSuccess(
        t("otherTrainingRecords.updateSuccessMsg")
      );
    });
  };

  const handleSubmitOtherTrainingRecord = async otherTrainingRecord => {
    if (!isSubmittingTrainingRecord) {
      setIsSubmittingTrainingRecord(true);
      const newOtherTrainingRecord = { ...otherTrainingRecord };
      if (newOtherTrainingRecord.certificate) {
        const uploadedFile = await uploadFile(
          newOtherTrainingRecord.certificate
        );
        newOtherTrainingRecord.certificate = uploadedFile.fileId;
      }
      if (currentTrainingRecord) {
        handleUpdateTrainingRecord(newOtherTrainingRecord);
      } else {
        handleAddNewTrainingRecord(newOtherTrainingRecord);
      }
    }
  };
  const localTime = new Date().getTimezoneOffset() / -60;

  const getPrintUrl = employeeId =>
    `${visionURL}/reports/8838/run_report?filter[user_id]=${employeeId}&invoke=print`;

  const getEmailUrl = employeeId =>
    `${visionURL}/learning_center/api/performance/transcript_reports/email?[employee_id]=${employeeId}&local_time=${localTime}`;

  return (
    <Box display="flex" flexDirection="column">
      <Box mb={3} display="flex" justifyContent="space-between">
        <TooltipButton
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => setOtherTRModalOpen(true)}
          data-qa="add-other-training-record-btn"
          tooltipProps={{
            title: isGoldOrPlatinum
              ? ""
              : t("administration.goldPlatinumFeatureTooltip")
          }}
          disabled={!isGoldOrPlatinum}
        >
          {t("otherTrainingRecords.addTitle")}
        </TooltipButton>
        <MyAchievementsButtons
          printButtonText={t("learningCenterPerformance.printButton")}
          emailButtonText={t("learningCenterPerformance.emailButton")}
          trainingObjectId="transcript"
          printUrl={getPrintUrl(employeeId)}
          emailUrl={getEmailUrl(employeeId)}
          employeeId={employeeId}
        />
      </Box>
      <Table
        itemType="record"
        columns={columns(t, openCourseProgressModal)}
        data={fetchTrainingRecords}
        onRowClick={(e, rowData) => handleRowClick(e, rowData)}
        tableRef={tableRef}
      />
      <CourseProgressModal
        setModalOpen={setViewDetailsModalOpen}
        open={viewDetailsModalOpen}
        trainingName={currentRecordName}
        loading={loading}
        currentProgress={currentProgress}
        completedActivities={completedActivities}
        totalCompletion={totalCompletion}
      />
      <OtherTrainingRecordModal
        open={otherTRModalOpen}
        onClose={() => handleClose()}
        submitTrainingRecord={handleSubmitOtherTrainingRecord}
        currentTrainingRecord={currentTrainingRecord}
        isSubmitting={isSubmittingTrainingRecord}
      />
    </Box>
  );
};

export default EmployeeTranscript;
